// The Audit log's words: each action as a line, who made it, and a short note of what changed, from
// hub.audit_log_page's rows (docs/data-model.md 10.4.2). From stage 5 the log holds ids, not names or
// emails (Adam, 4 Oct, answer 1): names are read now from the ids, and an anonymised person reads
// "Deleted person". Pure, so the words are checked on their own.
import { AUDIT_ACTIONS as REFERRAL_ACTIONS } from '../referrals/words.js'

export const ACTIONS = {
  person_created: 'Person added',
  grant: 'Access given',
  revoke: 'Access revoked',
  pause: 'Access paused',
  resume: 'Access resumed',
  extend: 'End date changed',
  change_cohort: 'Cohort changed',
  move_due_date: 'Balance date moved',
  grace_extended: '7 more days given',
  status_change: 'Status changed',
  card_link_sent: 'Card link sent',
  resend_login: 'Login email sent',
  log_out_everywhere: 'Logged out everywhere',
  move_email: 'Login email moved',
  merge: 'People merged',
  delete: 'Person deleted',
  role_change: 'Staff role changed',
  view_as_member_start: 'Started viewing as a member',
  view_as_member_stop: 'Stopped viewing as a member',
  hold_free: 'Free item held back',
  release_hold: 'Free item released',
  bulk_grant: 'Access given in bulk',
  bulk_undo: 'Bulk grant undone',
  certificate_by_hand: 'Certificate issued by hand',
  certificate_revoked: 'Certificate revoked',
  marked_messaged: 'Marked as messaged',
  flag_resolved: 'Job ticked off',
  payment_resolved: 'Payment applied',
  announcement_posted: 'Announcement posted',
  announcement_taken_down: 'Announcement taken down',
  product_created: 'Product added',
  // since stage 5's review also a product's move into the hub or a change to its address (owner only)
  product_state_changed: "Product's state or address changed",
  product_mode_changed: "Product's mode changed",
  layout_published: 'Page layout published',
  resource_removed: 'Resource removed',
  resource_restored: 'Resource put back',
  resource_replaced: "Resource's file replaced",
  recording_changed: 'Recording changed',
  price_map_changed: 'Price map changed',
  client_space_opened: '1-2-1 space opened',
  client_space_ended: '1-2-1 space ended',
  data_request_handled: 'Data request handled',
  unconfirmed_user_reset: 'Unconfirmed login reset',
  scope_change: "Host's products changed",
  two_step_reset: '2-step login reset',
  // a mastermind's goal and months (migration 20261006090000) and the accountability log (20261013210000)
  goal_locked: 'Goal locked',
  goal_unlocked: 'Goal unlocked',
  goal_reached: 'Goal marked reached',
  cycle_started: 'Month started',
  month_closed: 'Month closed',
  actions_added: 'Actions added',
  action_edited: "Action's words changed",
  action_set_aside: 'Action set aside',
  action_put_back: 'Action put back',
  action_outcome_corrected: 'How an action went corrected',
  catch_up_recorded: 'Catch-up recorded',
  attendance_changed: 'Register mark changed',
  win_added: 'Win added for a member',
  win_changed: "Member's win changed",
  win_deleted: "Member's win deleted",
  accountability_rules_changed: 'What on track means changed',
  accountability_log_shared: 'Accountability log sent or saved',
  // the Accelerator's admin (migration 80): a level set by hand and the open month's session
  accountability_level_set: 'Level set by hand',
  accountability_level_cleared: 'Level back to automatic',
  month_session_changed: "Month's session changed",
  // referrals (decision 55, migration 79): the 15 actions, in src/admin/referrals/words.js
  ...REFERRAL_ACTIONS,
}
export const actionWord = a => ACTIONS[a] || String(a || '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())

// The action filter, in the groups Adam looks for (BUILD-PLAN's list first: every grant, revoke,
// pause, merge, delete, role change and view as member)
export const ACTION_GROUPS = [
  ['grant', 'revoke', 'pause', 'resume', 'merge', 'delete', 'role_change', 'view_as_member_start', 'view_as_member_stop'],
  ['person_created', 'bulk_grant', 'bulk_undo', 'extend', 'change_cohort', 'move_due_date', 'grace_extended', 'status_change',
    'move_email', 'resend_login', 'card_link_sent', 'log_out_everywhere', 'hold_free', 'release_hold', 'scope_change',
    'certificate_by_hand', 'certificate_revoked', 'payment_resolved', 'flag_resolved', 'marked_messaged', 'data_request_handled',
    'unconfirmed_user_reset', 'two_step_reset'],
  ['product_created', 'product_state_changed', 'product_mode_changed', 'layout_published', 'price_map_changed', 'recording_changed',
    'resource_removed', 'resource_restored', 'resource_replaced', 'announcement_posted', 'announcement_taken_down',
    'client_space_opened', 'client_space_ended'],
  // Accountability: a mastermind's goals, months, registers, wins and logs
  ['goal_locked', 'goal_unlocked', 'goal_reached', 'cycle_started', 'month_closed', 'actions_added', 'action_edited',
    'action_set_aside', 'action_put_back', 'action_outcome_corrected', 'catch_up_recorded', 'attendance_changed',
    'win_added', 'win_changed', 'win_deleted', 'accountability_rules_changed', 'accountability_log_shared',
    'accountability_level_set', 'accountability_level_cleared', 'month_session_changed'],
  // Referrals (decision 55): codes, ticks, offers and introductions
  Object.keys(REFERRAL_ACTIONS),
]
export const ACTION_GROUP_NAMES = ['Access', 'People and jobs', 'Products', 'Accountability', 'Referrals']
export const ACTION_OPTIONS = ACTION_GROUPS.flat().map(a => ({ value: a, label: actionWord(a) }))

// Who made the change: a person's name, or what did it when no person did
export function actorWord(row) {
  if (row.actor_person_id) return row.actor_name || 'Someone no longer in the hub'
  switch (row.actor_kind) {
    case 'stripe': return 'Stripe'
    case 'import': return 'The import'
    case 'member': return 'The member'
    case 'staff': return 'A member of staff'
    default: return 'The hub'
  }
}

const SOURCES = { stripe: 'bought', manual: 'paid another way', import: 'imported', comp: 'free', test: 'a test login' }
// how long a grant was picked to last (Grant a product's and Grant in bulk's choices); until_date shows
// its date from ends_at
const LENGTHS = { same_as_buyer: 'same as a buyer', for_good: 'for good' }
// a level set by hand, as Masterminds says it (src/admin/mastermind/words.js MM.level)
const LEVEL_WORDS = { on_track: 'On track', a_little_behind: 'A little behind', behind: 'Behind', well_behind: 'Well behind' }
const STATUSES = {
  secured: 'place secured', active: 'active', overdue: 'overdue', suspended: 'suspended', paused: 'paused', ended: 'ended', revoked: 'revoked',
}
const COLUMN_WORDS = {
  full_name: 'name', first_name: 'first name', email: 'login email', role: 'staff role', phone: 'phone', photo_path: 'photo',
  status: 'status', ends_at: 'end date', cohort_id: 'cohort', balance_due_at: 'balance date', suspend_on: 'suspend date',
  paused_until: 'paused until', pause_reason: 'pause reason', reason: 'reason', is_published: 'switched on', label: 'label',
  sold_from: 'sold from', default_due_at: 'balance date', default_due_days: 'days to pay the balance', pay_button: 'pay button',
  balance_payment_url: 'payment link', state: 'live or draft', mode: 'mode', product_id: 'product', paused_by: 'paused by',
  moved_in: 'in the hub', slug: 'address', path: 'path',
}
const IGNORED = new Set(['id', 'created_at', 'updated_at', 'updated_by', 'updated_by_kind', 'updated_by_person_id', 'person_id', 'first_opened_at', 'promised_until'])

const short = v => {
  if (v === null || v === undefined || v === '') return 'nothing'
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (typeof v === 'object') return 'changed'
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10).split('-').reverse().join('/')
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.split('-').reverse().join('/')
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(s)) return 'another'
  return s.length > 40 ? `${s.slice(0, 37)}...` : s
}

// A short note of what changed: "Status: active to paused", "Changed: name, login email",
// "Free, from 24/09/2026". Never the whole row.
export function detailWord(row) {
  const before = row.before && typeof row.before === 'object' ? row.before : null
  const after = row.after && typeof row.after === 'object' ? row.after : null
  if (after && Array.isArray(after.changed)) {
    const cols = after.changed.map(c => COLUMN_WORDS[c] || c.replace(/_/g, ' '))
    return cols.length ? `Changed: ${cols.join(', ')}` : ''
  }
  // a 2-step reset holds counts only
  if (row.action === 'two_step_reset' && after) {
    const n = Number(after.sessions_ended) || 0
    return `${n} ${n === 1 ? 'session' : 'sessions'} ended`
  }
  // a level set by hand (migration 80): the levels only, never the note; the open month's session: the month
  if (row.action === 'accountability_level_set' && after) {
    const word = k => LEVEL_WORDS[k] || k
    // the note changed, the level kept: said so, never "On track to On track" (the review of 22 Oct)
    if (before?.level && before.level === after.level && after.note_changed) return `${word(after.level)}, note changed`
    return before?.level ? `${word(before.level)} to ${word(after.level)}` : word(after.level)
  }
  if (row.action === 'accountability_level_cleared') return before?.level ? `Was ${LEVEL_WORDS[before.level] || before.level}` : ''
  if (row.action === 'month_session_changed' && after?.month) return `Month ${after.month}`
  // a grant: how it was paid for, then how long it was picked to last where a grant kept it (item 195)
  if (row.action === 'grant' && after) {
    const how = SOURCES[after.source] || after.source
    const length = after.ends_at ? `until ${short(after.ends_at)}` : LENGTHS[after.length_rule] || null
    return [how ? how[0].toUpperCase() + how.slice(1) : null, length].filter(Boolean).join(', ')
  }
  if (before && after) {
    const keys = Object.keys(after).filter(k => !IGNORED.has(k) && JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    const blank = v => v === null || v === undefined || v === ''
    const bits = keys.slice(0, 4).map(k => {
      const word = COLUMN_WORDS[k] || k.replace(/_/g, ' ')
      const name = `${word[0].toUpperCase()}${word.slice(1)}`
      const b = k === 'status' ? (STATUSES[before[k]] || before[k]) : short(before[k])
      const a = k === 'status' ? (STATUSES[after[k]] || after[k]) : short(after[k])
      if (blank(before[k])) return `${name}: ${a}`
      if (blank(after[k])) return `${name}: ${b}, now cleared`
      return `${name}: ${b} to ${a}`
    })
    const more = keys.length > 4 ? `, and ${keys.length - 4} more` : ''
    return bits.join('. ') + more
  }
  return ''
}

// The page size the log reads, and the most it shows at once
export const PAGE = 50
